/**
 * Home-screen widget snapshot (pure). The app writes this JSON through the widget bridge
 * (apps/client/src/lib/widget.ts); the native widgets (apps/client/native-widgets/) only render it,
 * so all logic lives here and is unit-tested.
 */
import type { Drawing, PlaygroundConfig, PlaygroundItem } from './content/model';
import { hash } from './mailing';
import { levelFor, type ProgressState } from './progress';
import { localize, type Lang } from './text';
import { pickLocale, type LocaleTable } from './i18n/locales';

export const WIDGET_SNAPSHOT_KEY = 'sketchling.widget.v1';
export const WIDGET_APP_GROUP = 'group.app.sketchling.draw';

export interface WidgetSnapshot {
  v: 1;
  /** Local calendar day the prompt belongs to (YYYY-MM-DD). */
  day: string;
  lang: Lang;
  profileName: string | null;
  sparks: number;
  level: number;
  /** Sparks still needed for the next level (0 at max). */
  toNextLevel: number;
  prompt: { drawingId: string; title: string; minutes: number; deepLink: string } | null;
  /** Static strings in the snapshot language so native code never needs a dictionary. */
  labels: { heading: string; cta: string; sparks: string; empty: string };
  updatedAt: string;
}

const LABELS: LocaleTable<WidgetSnapshot['labels']> = {
  en: { heading: "Today's drawing", cta: 'Draw now', sparks: 'sparks', empty: 'Open Sketchling to pick a drawing' },
  vi: { heading: 'Tranh hôm nay', cta: 'Vẽ ngay', sparks: 'tia sáng', empty: 'Mở Sketchling để chọn tranh' },
  'zh-Hans': { heading: '今天画什么', cta: '马上画', sparks: '星火', empty: '打开 Sketchling 挑一幅画吧' },
};

const pad = (n: number) => String(n).padStart(2, '0');
export const localDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * Daily prompt: a deterministic pick per (day, profile) among playground items the profile has not
 * drawn yet (all items once everything is done). Free items first for free users.
 */
export function dailyPrompt(
  playground: PlaygroundConfig,
  progress: ProgressState,
  day: string,
  seed: string,
  opts: { premium: boolean },
): PlaygroundItem | null {
  const all = playground.items.filter((i) => opts.premium || i.free || playground.previewItemIds.includes(i.id));
  const pool0 = all.length ? all : playground.items;
  const done = new Set(progress.completedPlaygroundItemIds);
  const fresh = pool0.filter((i) => !done.has(i.id));
  const pool = fresh.length ? fresh : pool0;
  if (!pool.length) return null;
  return pool[hash(`${day}:${seed}`) % pool.length]!;
}

export function widgetSnapshot(input: {
  now: Date;
  lang: Lang;
  profileId: string | null;
  profileName: string | null;
  progress: ProgressState;
  playground: PlaygroundConfig;
  drawings: Map<string, Drawing>;
  premium: boolean;
}): WidgetSnapshot {
  const day = localDay(input.now);
  const lv = levelFor(input.progress.totalSparks);
  const item = input.profileId ? dailyPrompt(input.playground, input.progress, day, input.profileId, { premium: input.premium }) : null;
  const drawing = item ? input.drawings.get(item.drawingId) : undefined;
  return {
    v: 1,
    day,
    lang: input.lang,
    profileName: input.profileName,
    sparks: input.progress.totalSparks,
    level: lv.level,
    toNextLevel: lv.toNext,
    prompt:
      item && drawing
        ? { drawingId: drawing.id, title: localize(drawing.title, input.lang), minutes: drawing.minutes, deepLink: `/lesson/${item.miniUnitId}` }
        : null,
    labels: pickLocale(LABELS, input.lang),
    updatedAt: input.now.toISOString(),
  };
}
